import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus, Layers, Trash2, Edit2, FileDown, FileText, Save, X, ChevronRight, ChevronDown, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Cyber Security", "Safeguarding", "Custom"];

export default function Stax() {
  return (
    <div className="space-y-6" data-testid="stax-catalog-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">STAX</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Stax Services</h1>
        <p className="text-slate-500 text-sm mt-1">
          Master list of Xentra Stax products, detailed service descriptions, and a builder that
          creates a customer-specific service schedule.
        </p>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-stax-products">Products</TabsTrigger>
          <TabsTrigger value="descriptions" data-testid="tab-stax-descriptions">Service Descriptions</TabsTrigger>
          <TabsTrigger value="builder" data-testid="tab-stax-builder">Schedule Builder</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-4"><ProductsTab /></TabsContent>
        <TabsContent value="descriptions" className="mt-4"><DescriptionsTab /></TabsContent>
        <TabsContent value="builder" className="mt-4"><BuilderTab /></TabsContent>
      </Tabs>
    </div>
  );
}


/* ============================================================
   Tab 1 — Products (existing behaviour, unchanged)
   ============================================================ */
function ProductsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Cyber Security", description: "" });

  const load = async () => {
    const r = await api.get("/stax-products");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (editing) {
      await api.patch(`/stax-products/${editing.id}`, form);
      toast.success("Updated");
    } else {
      await api.post(`/stax-products`, form);
      toast.success("Added");
    }
    setOpen(false); setEditing(null);
    setForm({ name: "", category: "Cyber Security", description: "" });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Archive this Stax product?")) return;
    await api.delete(`/stax-products/${id}`);
    load();
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, category: it.category, description: it.description || "" });
    setOpen(true);
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name:"", category:"Cyber Security", description:"" }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-stax-btn" className="gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> New Product</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Stax product</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="stax-form-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="stax-form-submit" onClick={save} className="bg-blue-600 hover:bg-blue-700">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card className="p-10 text-center border-slate-200">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500 text-sm">No products yet.</div>
        </Card>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cat, list]) => (
            <Card key={cat} className="border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-blue-600" /> {cat} STAX
                  <span className="text-xs font-mono text-slate-400 ml-2">{list.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {list.map((it) => (
                    <div key={it.id} className="p-3 rounded-md border border-slate-100 hover:border-blue-200 bg-white transition-colors flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">{it.name}</div>
                        {it.description && <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>}
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(it)}>
                          <Edit2 className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(it.id)}>
                          <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


/* ============================================================
   Tab 2 — Service Descriptions (catalogue of long-form services)
   ============================================================ */
const BLANK_SERVICE = {
  service_code: "", category: "", name: "",
  paragraphs: [""], bullets: [""],
};

function DescriptionsTab() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_SERVICE);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    const r = await api.get("/stax-services");
    setServices(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(BLANK_SERVICE);
    setOpen(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      service_code: s.service_code || "",
      category: s.category || "",
      name: s.name || "",
      paragraphs: (s.paragraphs && s.paragraphs.length ? s.paragraphs : [""]),
      bullets:    (s.bullets    && s.bullets.length    ? s.bullets    : [""]),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.service_code.trim()) return toast.error("Service code (e.g. 1.1) is required");
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      ...form,
      service_code: form.service_code.trim(),
      category: form.category.trim(),
      name: form.name.trim(),
      paragraphs: form.paragraphs.map((s) => s.trim()).filter(Boolean),
      bullets:    form.bullets.map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.patch(`/stax-services/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/stax-services", payload);
        toast.success("Service added");
      }
      setOpen(false); setEditing(null); setForm(BLANK_SERVICE);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Archive "${s.service_code} ${s.name}"?`)) return;
    await api.delete(`/stax-services/${s.id}`);
    toast.success("Archived");
    await load();
  };

  const grouped = useMemo(() => {
    return services.reduce((acc, s) => {
      const k = s.category || "Uncategorised";
      (acc[k] = acc[k] || []).push(s);
      return acc;
    }, {});
  }, [services]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          Full-detail descriptions used to build service schedules. Currently <span className="font-semibold">{services.length}</span> service{services.length === 1 ? "" : "s"} in the catalogue.
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="stax-svc-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="stax-svc-add">
            <Plus className="h-4 w-4" /> New service
          </Button>
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card className="p-10 text-center border-slate-200">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500 text-sm">No services yet.</div>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <Card key={cat} className="border-slate-200" data-testid={`stax-svc-cat-${cat}`}>
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-base font-heading">{cat}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {list.map((s) => {
                  const isOpen = !!expanded[s.id];
                  return (
                    <div key={s.id} className="border-b border-slate-100 last:border-b-0" data-testid={`stax-svc-row-${s.service_code}`}>
                      <div className="p-3 flex items-start justify-between gap-3 hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [s.id]: !e[s.id] }))}>
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 mt-0.5" /> : <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5" />}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900">
                              <span className="font-mono text-blue-700 mr-2">{s.service_code}</span>
                              {s.name}
                            </div>
                            {!isOpen && s.paragraphs?.[0] && (
                              <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{s.paragraphs[0]}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`stax-svc-edit-${s.service_code}`}>
                            <Edit2 className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`stax-svc-delete-${s.service_code}`}>
                            <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" />
                          </Button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="px-8 pb-4 pt-1 space-y-2 text-sm text-slate-700 bg-slate-50/60">
                          {s.paragraphs?.map((p, i) => <p key={i}>{p}</p>)}
                          {s.bullets?.length > 0 && (
                            <ul className="list-disc pl-5 space-y-1">
                              {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(BLANK_SERVICE); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit service" : "New service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Code</Label>
                <Input placeholder="e.g. 1.1" value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} data-testid="stax-svc-form-code" />
              </div>
              <div className="col-span-2">
                <Label>Category</Label>
                <Input placeholder="e.g. 1. Detection and Response" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="stax-svc-form-category" />
              </div>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="stax-svc-form-name" />
            </div>
            <div>
              <Label>Paragraphs</Label>
              {form.paragraphs.map((p, i) => (
                <div key={i} className="flex gap-2 mt-2">
                  <Textarea rows={3} value={p} onChange={(e) => {
                    const arr = [...form.paragraphs]; arr[i] = e.target.value;
                    setForm({ ...form, paragraphs: arr });
                  }} data-testid={`stax-svc-form-para-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => {
                    const arr = form.paragraphs.filter((_, ix) => ix !== i);
                    setForm({ ...form, paragraphs: arr.length ? arr : [""] });
                  }}><X className="h-4 w-4 text-slate-400" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({ ...form, paragraphs: [...form.paragraphs, ""] })} data-testid="stax-svc-form-add-para">
                <Plus className="h-3 w-3 mr-1" /> Add paragraph
              </Button>
            </div>
            <div>
              <Label>Bullet points <span className="text-xs text-slate-400">(optional)</span></Label>
              {form.bullets.map((b, i) => (
                <div key={i} className="flex gap-2 mt-2">
                  <Input value={b} onChange={(e) => {
                    const arr = [...form.bullets]; arr[i] = e.target.value;
                    setForm({ ...form, bullets: arr });
                  }} data-testid={`stax-svc-form-bullet-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => {
                    const arr = form.bullets.filter((_, ix) => ix !== i);
                    setForm({ ...form, bullets: arr.length ? arr : [""] });
                  }}><X className="h-4 w-4 text-slate-400" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({ ...form, bullets: [...form.bullets, ""] })} data-testid="stax-svc-form-add-bullet">
                <Plus className="h-3 w-3 mr-1" /> Add bullet
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={save} data-testid="stax-svc-form-save">
              <Save className="h-4 w-4" /> {editing ? "Save changes" : "Create service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ============================================================
   Tab 3 — Schedule Builder
   ============================================================ */
function BuilderTab() {
  const [services, setServices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState([]);

  const [form, setForm] = useState({
    account_id: "",
    customer_name: "",
    customer_address: "",
    effective_date: "",
    reference: "",
    xentra_signatory: "",
    customer_signatory: "",
    notes: "",
  });

  const load = async () => {
    const [svcs, accs] = await Promise.all([
      api.get("/stax-services"),
      api.get("/accounts").catch(() => ({ data: [] })),
    ]);
    setServices(svcs.data || []);
    setAccounts(accs.data || []);
    // Preselect all services by default — matches the "vanilla with all services on" starting point.
    setSelectedIds(new Set((svcs.data || []).map((s) => s.id)));
  };
  useEffect(() => { load(); }, []);

  // When an account is picked, pre-fill the customer details and load saved schedules.
  useEffect(() => {
    if (!form.account_id) { setSaved([]); return; }
    const acc = accounts.find((a) => a.id === form.account_id);
    if (acc) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || acc.name || "",
        customer_address: f.customer_address || [acc.address_line1, acc.address_line2, acc.city, acc.postcode].filter(Boolean).join(", "),
      }));
    }
    api.get(`/stax-schedules?account_id=${encodeURIComponent(form.account_id)}`)
      .then((r) => setSaved(r.data || []))
      .catch(() => setSaved([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.account_id, accounts]);

  const grouped = useMemo(() => {
    return services.reduce((acc, s) => {
      const k = s.category || "Uncategorised";
      (acc[k] = acc[k] || []).push(s);
      return acc;
    }, {});
  }, [services]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleCategory = (list, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      list.forEach((s) => { if (checked) next.add(s.id); else next.delete(s.id); });
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(services.map((s) => s.id)));
  const selectNone = () => setSelectedIds(new Set());

  const buildPayload = () => ({
    ...form,
    account_id: form.account_id || null,
    service_ids: Array.from(selectedIds),
  });

  const generate = async (fmt) => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (selectedIds.size === 0) return toast.error("Tick at least one service");
    setBusy(true);
    try {
      const r = await api.post(`/stax-schedules/generate?format=${fmt}`, buildPayload(), { responseType: "blob" });
      const blob = new Blob([r.data], { type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Xentra STAX Service Schedule - ${form.customer_name.trim()}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} generated`);
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text())?.detail; } catch { /* noop */ }
      }
      toast.error(detail || `Failed to generate ${fmt.toUpperCase()}`);
    } finally { setBusy(false); }
  };

  const saveOnAccount = async () => {
    if (!form.account_id) return toast.error("Pick an account to save the schedule against");
    if (selectedIds.size === 0) return toast.error("Tick at least one service");
    try {
      await api.post("/stax-schedules", buildPayload());
      toast.success("Schedule saved on the account");
      const r = await api.get(`/stax-schedules?account_id=${encodeURIComponent(form.account_id)}`);
      setSaved(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const removeSaved = async (id) => {
    if (!window.confirm("Delete this saved schedule?")) return;
    await api.delete(`/stax-schedules/${id}`);
    setSaved(saved.filter((s) => s.id !== id));
    toast.success("Deleted");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
      {/* Left column — customer details + services */}
      <div className="space-y-4">
        <Card className="border-slate-200" data-testid="schedule-cover">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Customer details</CardTitle></CardHeader>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Prefill from account <span className="text-xs text-slate-400">(optional)</span></Label>
              <Select value={form.account_id || "__none__"} onValueChange={(v) => setForm({ ...form, account_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="schedule-account-select"><SelectValue placeholder="Select an account" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None (manual entry) —</SelectItem>
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="schedule-customer-name" /></div>
            <div><Label>Reference / SOW no.</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="schedule-reference" /></div>
            <div className="md:col-span-2"><Label>Customer address</Label><Input value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="schedule-address" /></div>
            <div><Label>Effective date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="schedule-effective-date" /></div>
            <div><Label>Xentra signatory</Label><Input value={form.xentra_signatory} onChange={(e) => setForm({ ...form, xentra_signatory: e.target.value })} data-testid="schedule-xentra-signatory" /></div>
            <div><Label>Customer signatory</Label><Input value={form.customer_signatory} onChange={(e) => setForm({ ...form, customer_signatory: e.target.value })} data-testid="schedule-customer-signatory" /></div>
            <div className="md:col-span-2"><Label>Notes <span className="text-xs text-slate-400">(optional)</span></Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="schedule-notes" /></div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between gap-3">
            <CardTitle className="text-base font-heading">Services to include</CardTitle>
            <div className="flex gap-2 text-xs">
              <button className="text-blue-700 hover:underline" onClick={selectAll} data-testid="schedule-select-all">Select all</button>
              <span className="text-slate-300">|</span>
              <button className="text-slate-600 hover:underline" onClick={selectNone} data-testid="schedule-select-none">Select none</button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-5">
            {Object.keys(grouped).length === 0 ? (
              <div className="text-slate-500 text-sm">No services yet — add them under "Service Descriptions".</div>
            ) : Object.entries(grouped).map(([cat, list]) => {
              const catCount = list.length;
              const selInCat = list.filter((s) => selectedIds.has(s.id)).length;
              return (
                <div key={cat}>
                  <label className="flex items-center gap-2 mb-2 cursor-pointer">
                    <Checkbox
                      checked={selInCat === catCount ? true : selInCat === 0 ? false : "indeterminate"}
                      onCheckedChange={(v) => toggleCategory(list, !!v)}
                      data-testid={`schedule-cat-toggle-${cat}`}
                    />
                    <span className="font-semibold text-slate-900">{cat}</span>
                    <span className="text-xs text-slate-400">{selInCat}/{catCount}</span>
                  </label>
                  <div className="ml-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {list.map((s) => (
                      <label key={s.id} className="flex items-start gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer" data-testid={`schedule-svc-${s.service_code}`}>
                        <Checkbox checked={selectedIds.has(s.id)} onCheckedChange={() => toggle(s.id)} />
                        <div className="text-sm">
                          <div><span className="font-mono text-blue-700 mr-1">{s.service_code}</span>{s.name}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Right column — actions + saved schedules */}
      <div className="space-y-4">
        <Card className="border-slate-200 sticky top-4">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Generate</CardTitle></CardHeader>
          <CardContent className="p-4 space-y-2">
            <div className="text-xs text-slate-500 mb-1">Selected: <span className="font-semibold text-slate-800">{selectedIds.size}</span> of {services.length} services</div>
            <Button className="w-full gap-2 bg-blue-600 hover:bg-blue-700" onClick={() => generate("pdf")} disabled={busy} data-testid="schedule-generate-pdf">
              <FileDown className="h-4 w-4" /> Download PDF
            </Button>
            <Button variant="outline" className="w-full gap-2" onClick={() => generate("docx")} disabled={busy} data-testid="schedule-generate-docx">
              <FileText className="h-4 w-4" /> Download Word (.docx)
            </Button>
            {form.account_id && (
              <Button variant="outline" className="w-full gap-2" onClick={saveOnAccount} data-testid="schedule-save">
                <Save className="h-4 w-4" /> Save on account
              </Button>
            )}
          </CardContent>
        </Card>

        {form.account_id && (
          <Card className="border-slate-200" data-testid="schedule-saved-list">
            <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading">Saved for this account</CardTitle></CardHeader>
            <CardContent className="p-2">
              {saved.length === 0 ? (
                <div className="p-3 text-xs text-slate-500">No saved schedules yet.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {saved.map((s) => (
                    <li key={s.id} className="p-3 flex items-start justify-between gap-2 text-sm" data-testid={`schedule-saved-${s.id}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{s.reference || s.customer_name}</div>
                        <div className="text-xs text-slate-500">
                          {(s.service_ids || []).length} services · {(s.created_at || "").slice(0, 10)} · by {s.created_by || "—"}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeSaved(s.id)}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" /></Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
